import {
  getMonthlyLiabilityAnalysis,
  getMonthlyVarianceAnalysis,
} from '@/api/dashboardApi'
import { monthyWHTTrendUIMetaData } from '@/constants/dashboardConstant'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import CommonRadioButton from '../common/CommonRadioButton'

const VarianceTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  const dataPoint = payload[0]?.payload
  if (!dataPoint) return null
  // Year comes from the hovered data point in chartData.
  const year = dataPoint.year
  const labelSplit = label ? label.split('-') : []
  const month = labelSplit[0]
  return (
    <Box
      sx={{
        backgroundColor: 'var(--forbrand-primary)',
        borderRadius: '4px',
        p: 1.5,
        minWidth: 200,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto',
          gap: '4px 40px',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--white-color)',
            pb: 0.5,
          }}
        >
          {month} {year}
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            pb: 0.5,
            textAlign: 'right',
          }}
        >
          Records
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            pb: 0.5,
            textAlign: 'right',
          }}
        >
          Amount
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: 'var(--chart-orange)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
            }}
          >
            Short deduction:
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.shortDeductionCountDisplay}
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.shortDeductionAmountDisplay}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: 'var(--chart-peach)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
            }}
          >
            Excess deduction:
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.excessDeductionCountDisplay}
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.excessDeductionAmountDisplay}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: 'var(--forbrand-color)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
            }}
          >
            Total:
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.totalRecordsDisplay}
        </Typography>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--white-color)',
            textAlign: 'right',
          }}
        >
          {dataPoint.totalVarianceAmountDisplay}
        </Typography>
      </Box>
    </Box>
  )
}

const LiabilityTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null
  const dataPoint = payload[0]?.payload
  if (!dataPoint) return null
  // Year comes from the hovered data point in chartData.
  const year = dataPoint.year
  const labelSplit = label ? label.split('-') : []
  const month = labelSplit[0]
  return (
    <Box
      sx={{
        backgroundColor: 'var(--forbrand-primary)',
        borderRadius: '4px',
        p: 1.5,
        minWidth: 200,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          sx={{
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            color: 'var(--white-color)',
            pb: 0.5,
          }}
        >
          {month} {year}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: 'var(--chart-orange)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
            }}
          >
            Expense liable to WHT: {dataPoint.expenseLiableToWht}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              backgroundColor: 'var(--chart-peach)',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--white-color)',
            }}
          >
            WHT amount: {dataPoint.whtAmount}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

// Plotted bar height (chart units) given to the shortest non-zero bar (rank 1),
// so every month that has a value is clearly visible.
const MIN_BAR_HEIGHT = 12
// Plotted bar height given to the tallest bar (the top-ranked total). The
// rank-based heights spread evenly between MIN_BAR_HEIGHT and MAX_BAR_HEIGHT.
const MAX_BAR_HEIGHT = 100
// Minimum share of a bar's height guaranteed to a non-zero segment so a tiny
// sliver (e.g. a segment that is ~1% of the month's total) stays visible. The
// larger segment gives up the difference, so the two still sum to the bar.
const MIN_SEGMENT_SHARE = 0.04

// Maps each month's REAL total (sum of its two segments) to a bar height by its
// RANK across all months, so any difference in total — however small — changes
// the rank and therefore the bar height (no two unequal totals tie). Returns a
// Map of originalIndex -> bar height. Zero totals are excluded (no bar). Ranks
// are dense (ties share a rank), heights spread evenly across
// [MIN_BAR_HEIGHT, MAX_BAR_HEIGHT].
const buildRankedHeightByIndex = totals => {
  // Keep original index alongside each non-zero total.
  const nonZero = totals
    .map((total, index) => ({ index, total }))
    .filter(entry => entry.total > 0)
  const heightByIndex = new Map()
  if (nonZero.length === 0) return heightByIndex
  // Ascending sort so the smallest total ranks lowest (shortest bar).
  const sorted = [...nonZero].sort((a, b) => a.total - b.total)
  // Dense ranking: equal totals share a rank; distinct totals step by one.
  let rank = 0
  let previousTotal = null
  const rankByIndex = new Map()
  sorted.forEach(entry => {
    if (previousTotal === null || entry.total !== previousTotal) {
      rank += 1
      previousTotal = entry.total
    }
    rankByIndex.set(entry.index, rank)
  })
  const maxRank = rank
  nonZero.forEach(entry => {
    // maxRank === 1 → all equal; give them the max height.
    const fraction =
      maxRank > 1 ? (rankByIndex.get(entry.index) - 1) / (maxRank - 1) : 1
    const height = MIN_BAR_HEIGHT + fraction * (MAX_BAR_HEIGHT - MIN_BAR_HEIGHT)
    heightByIndex.set(entry.index, height)
  })
  return heightByIndex
}

// Splits a bar's ranked height between its two segments in proportion to their
// REAL values, so the within-bar ratio is truthful and consistent with the
// actual amounts. Guarantees each non-zero segment at least MIN_SEGMENT_SHARE of
// the bar so tiny slivers stay visible; the larger segment gives up the
// difference so the two always sum to `barHeight`.
const splitByRealRatio = (firstValue, secondValue, barHeight) => {
  const first = Number(firstValue) > 0 ? Number(firstValue) : 0
  const second = Number(secondValue) > 0 ? Number(secondValue) : 0
  const realTotal = first + second
  if (barHeight <= 0 || realTotal <= 0) {
    return { firstPlot: 0, secondPlot: 0 }
  }
  // Only one side present → it fills the whole bar.
  if (first === 0) return { firstPlot: 0, secondPlot: barHeight }
  if (second === 0) return { firstPlot: barHeight, secondPlot: 0 }
  const minSegment = barHeight * MIN_SEGMENT_SHARE
  let firstPlot = barHeight * (first / realTotal)
  let secondPlot = barHeight - firstPlot
  if (firstPlot < minSegment) {
    firstPlot = minSegment
    secondPlot = barHeight - firstPlot
  } else if (secondPlot < minSegment) {
    secondPlot = minSegment
    firstPlot = barHeight - secondPlot
  }
  return { firstPlot, secondPlot }
}

// Prepares liability data for the stacked chart. The bar height is ranked by the
// month's real total (expense + wht) across all months, and that height is split
// between the two segments by their real ratio (see buildRankedHeightByIndex /
// splitByRealRatio). Adds plot-only keys (`expenseLiableToWhtPlot`,
// `whtAmountPlot`) plus `recordsDotValue` (the bar's ranked height) so the
// records dot sits on top. When a point's records count (`totalRecords`,
// numeric) is 0, `recordsDotValue` is null so Recharts skips the dot. The true
// values (`expenseLiableToWhtValue`, `whtAmountValue`, `totalRecordsValue`) are
// left untouched for the tooltip and labels — `totalRecordsValue` is the display
// string.
const withLiabilityRecordsDot = (data = []) => {
  const totals = data.map(
    point =>
      (Number(point.expenseLiableToWhtValue) || 0) +
      (Number(point.whtAmountValue) || 0)
  )
  const heightByIndex = buildRankedHeightByIndex(totals)
  return data.map((point, index) => {
    const barHeight = heightByIndex.get(index) || 0
    const { firstPlot, secondPlot } = splitByRealRatio(
      point.expenseLiableToWhtValue,
      point.whtAmountValue,
      barHeight
    )
    // The records line/dot spans every month. Months with no records sit at
    // the baseline (0) so the line stays continuous across the whole range.
    return {
      ...point,
      expenseLiableToWhtPlot: firstPlot,
      whtAmountPlot: secondPlot,
      recordsDotValue: barHeight,
    }
  })
}

// Prepares variance data for the stacked chart. The bar height is ranked by the
// month's real total (short + excess) across all months, and that height is
// split between the two segments by their real ratio (see
// buildRankedHeightByIndex / splitByRealRatio). Adds plot-only keys
// (`shortDeductionPlot`, `excessDeductionPlot`) plus `recordsDotValue` (the
// bar's ranked height) so the records dot sits on top. When a point's records
// count (`totalRecords`, numeric) is 0, `recordsDotValue` is null so Recharts
// skips both the dot and the connecting line segment at that point. The true
// values (`shortDeductionAmount`, `excessDeductionAmount`, `totalRecordsDisplay`)
// are left untouched for the tooltip and labels — `totalRecordsDisplay` is the
// display string.
const withVarianceRecordsDot = (data = []) => {
  // Bar height is ranked by the backend's totalVarianceAmount (the source of
  // truth for the month's total), which may differ slightly from
  // short + excess due to rounding. Fall back to the segment sum only when
  // totalVarianceAmount is absent.
  const totals = data.map(point => {
    const total = Number(point.totalVarianceAmount)
    if (Number.isFinite(total)) return total
    return (
      (Number(point.shortDeductionAmount) || 0) +
      (Number(point.excessDeductionAmount) || 0)
    )
  })
  const heightByIndex = buildRankedHeightByIndex(totals)
  return data.map((point, index) => {
    const barHeight = heightByIndex.get(index) || 0
    const { firstPlot, secondPlot } = splitByRealRatio(
      point.shortDeductionAmount,
      point.excessDeductionAmount,
      barHeight
    )
    // The records line/dot spans every month. Months with no records sit at
    // the baseline (0) so the line stays continuous across the whole range.
    return {
      ...point,
      shortDeductionPlot: firstPlot,
      excessDeductionPlot: secondPlot,
      recordsDotValue: barHeight,
    }
  })
}

const MonthlyWHTTrend = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [trendViewMode, setTrendViewMode] = useState('variance')
  // Trend datasets. Each is fetched lazily and cached so switching the radio
  // doesn't re-request data that's already loaded for the current filters.
  const [varianceData, setVarianceData] = useState([])
  const [liabilityData, setLiabilityData] = useState([])
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Derived Data ====================
  // Fetches the trend dataset for the given view mode using the committed
  // filters. Resolves to { viewMode, data }; does NOT set state.
  const fetchTrendData = useCallback(
    async mode => {
      const request =
        mode === 'variance'
          ? getMonthlyVarianceAnalysis
          : getMonthlyLiabilityAnalysis
      const response = await request(appliedFilters)
      const data =
        response?.success && response.data?.data?.dataPoints
          ? response.data.data.dataPoints
          : []
      return { viewMode: mode, data }
    },
    [appliedFilters]
  )
  const isVariance = trendViewMode === 'variance'
  const activeLegend = isVariance
    ? monthyWHTTrendUIMetaData.varianceLegend
    : monthyWHTTrendUIMetaData.liabilityLegend
  const chartData = isVariance
    ? withVarianceRecordsDot(varianceData)
    : withLiabilityRecordsDot(liabilityData)

  // ==================== Effects ====================
  // Fetch on initial options load and whenever the committed filters change
  // (Apply / Reset). Shows the loader each time.
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const result = await fetchTrendData(trendViewMode)
        applyTrendResult(result)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
    // trendViewMode is intentionally omitted — radio changes are handled by
    // handleViewModeChange, not a full dashboard reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsLoaded, appliedFilters])

  // ==================== Handlers ====================
  // Radio change: switch the view and lazily fetch that view's dataset only if
  // it isn't already loaded for the current filters.
  const handleViewModeChange = async mode => {
    setTrendViewMode(mode)
    setDataLoaded(false)
    try {
      const result = await fetchTrendData(mode)
      applyTrendResult(result)
    } finally {
      setDataLoaded(true)
    }
  }

  // ==================== Component Helpers ====================
  const applyTrendResult = ({ viewMode, data }) => {
    if (viewMode === 'variance') {
      setVarianceData(data)
    } else {
      setLiabilityData(data)
    }
  }

  // ==================== Return ====================
  return (
    <Box
      sx={{
        border: '1px solid var(--border-subtel)',
        backgroundColor: 'var(--white-color)',
        width: '100%',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box
          component="section"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 500,
              color: 'var(--forbrand-primary)',
              lineHeight: 1.4,
            }}
          >
            {monthyWHTTrendUIMetaData.heading}
          </Typography>

          <Tooltip title={monthyWHTTrendUIMetaData.subHeading}>
            <InfoOutlinedIcon sx={{ width: '17.5px', height: '17.47px' }} />
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CommonRadioButton
              label="Variance analysis"
              checked={trendViewMode === 'variance'}
              onChange={() => handleViewModeChange('variance')}
            />
            <CommonRadioButton
              label="Liability analysis"
              checked={trendViewMode === 'liability'}
              onChange={() => handleViewModeChange('liability')}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            {activeLegend.map(item => (
              <Box
                key={item.label}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    backgroundColor: item.color,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--forbrand-primary)',
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {!dataLoaded ? (
        <Box
          sx={{
            width: '100%',
            height: 240,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--forbrand-primary)',
              fontSize: 'var(--font-size-lg)',
            }}
          >
            Loading...
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="var(--border-grid-light)"
              />
              <XAxis
                dataKey="monthLabel"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: 'var(--forbrand-secondary)' }}
              />
              <YAxis
                hide
                yAxisId="bars"
                // Guard the top so an all-zero dataset still has a positive
                // domain; otherwise [0, 0] is degenerate and Recharts floats the
                // dots/line mid-chart instead of resting them on the baseline.
                domain={[
                  0,
                  dataMax => Math.max(dataMax * 1.15, MAX_BAR_HEIGHT),
                ]}
              />

              {trendViewMode === 'variance' ? (
                <>
                  <RechartsTooltip
                    content={<VarianceTooltip />}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  />
                  <Bar
                    dataKey="shortDeductionPlot"
                    stackId="stack"
                    fill="var(--chart-orange)"
                    barSize={32}
                    yAxisId="bars"
                    name="Short deduction"
                  />
                  <Bar
                    dataKey="excessDeductionPlot"
                    stackId="stack"
                    fill="var(--chart-peach)"
                    barSize={32}
                    yAxisId="bars"
                    name="Excess deduction"
                  />
                  <Line
                    type="linear"
                    dataKey="recordsDotValue"
                    stroke="var(--forbrand-color)"
                    strokeWidth={2}
                    connectNulls={true}
                    dot={{ fill: 'var(--forbrand-color)', r: 4 }}
                    activeDot={{ r: 5 }}
                    yAxisId="bars"
                    name="Records"
                  >
                    <LabelList
                      dataKey="totalRecordsDisplay"
                      position="top"
                      style={{
                        fontSize: 12,
                        fill: 'var(--forbrand-primary)',
                        fontWeight: 400,
                      }}
                      offset={8}
                    />
                  </Line>
                </>
              ) : (
                <>
                  <RechartsTooltip
                    content={<LiabilityTooltip />}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  />
                  <Bar
                    dataKey="expenseLiableToWhtPlot"
                    stackId="stack"
                    fill="var(--chart-orange)"
                    barSize={32}
                    yAxisId="bars"
                    name="Expense liable to WHT"
                  />
                  <Bar
                    dataKey="whtAmountPlot"
                    stackId="stack"
                    fill="var(--chart-peach)"
                    barSize={32}
                    yAxisId="bars"
                    name="WHT amount"
                  />
                  <Line
                    type="linear"
                    dataKey="recordsDotValue"
                    stroke="none"
                    dot={{ fill: 'var(--forbrand-color)', r: 4 }}
                    activeDot={{ r: 5 }}
                    yAxisId="bars"
                    name="Total records"
                  >
                    <LabelList
                      dataKey="totalRecordsValue"
                      position="top"
                      style={{
                        fontSize: 12,
                        fill: 'var(--forbrand-primary)',
                        fontWeight: 400,
                      }}
                      offset={8}
                    />
                  </Line>
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  )
}

export default MonthlyWHTTrend
